import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherX } from "@subframe/core";
import axios from "axios";
import { useState } from "react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { sanitizePin } from "@/features/device-setup/pin-login-api";
import { useManagerSetUserPinCommandControllerSetPin } from "@/generated/users/users";

import { useInvalidateAccount } from "./use-invalidate-account";

const PIN_LENGTH = 4;
const FORBIDDEN = "You don't have permission to reset this user's PIN.";
const GENERIC_ERROR = "Something went wrong. Please try again.";

interface SetPinDialogProps {
  userId: string;
  displayName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Manager/admin action to reset a user's PIN to a temporary one. Clears any
 * lock, so this is also the "unblock a locked practitioner" path. The user
 * enters the temporary PIN on their next login and must then choose their own.
 */
export function SetPinDialog({
  userId,
  displayName,
  open,
  onOpenChange,
}: SetPinDialogProps) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const setUserPin = useManagerSetUserPinCommandControllerSetPin();
  const invalidateAccount = useInvalidateAccount();
  const isSaving = setUserPin.isPending;

  function close() {
    if (isSaving) return;
    setPin("");
    setError(null);
    onOpenChange(false);
  }

  async function handleSave() {
    if (pin.length !== PIN_LENGTH) return;
    setError(null);
    try {
      await setUserPin.mutateAsync({ id: userId, data: { temporaryPin: pin } });
      toast.success(`Temporary PIN set for ${displayName}.`);
      setPin("");
      onOpenChange(false);
      await invalidateAccount(userId);
    } catch (err) {
      setError(
        axios.isAxiosError(err) && err.response?.status === 403
          ? FORBIDDEN
          : GENERIC_ERROR,
      );
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
    >
      <DialogContent showCloseButton={false} className="max-w-md p-0">
        <DialogTitle className="sr-only">Reset PIN</DialogTitle>

        <div className="flex items-start justify-between border-b border-solid border-secondary-beige-200 px-6 py-4">
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Reset PIN
            </span>
            <span className="text-body font-body text-subtext-color">
              Reset {displayName}&apos;s PIN? Set a temporary 4-digit PIN and
              share it with them — they&apos;ll be asked to choose a new one on
              their next login. This clears any lockout.
            </span>
          </div>
          <IconButton
            variant="neutral-tertiary"
            size="medium"
            icon={<FeatherX />}
            disabled={isSaving}
            onClick={close}
          />
        </div>

        <form
          className="flex flex-col gap-6 px-6 py-6"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
        >
          <TextField
            className="h-auto w-full"
            size="large"
            label="Temporary PIN"
          >
            <TextField.Input
              inputMode="numeric"
              autoComplete="off"
              autoFocus
              placeholder="4-digit temporary PIN"
              value={pin}
              disabled={isSaving}
              onChange={(e) => {
                setPin(sanitizePin(e.target.value, PIN_LENGTH));
                setError(null);
              }}
            />
          </TextField>

          {error && (
            <span className="text-body font-body text-error-700">{error}</span>
          )}

          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="neutral-primary"
              size="large"
              disabled={isSaving}
              onClick={close}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="brand-primary"
              size="large"
              disabled={isSaving || pin.length !== PIN_LENGTH}
              loading={isSaving}
            >
              Reset PIN
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
